export type LanguageCode =
  'english' | 'spanish' | 'french' | 'german' | 'hindi' | 'portuguese' | 'italian' | 'russian';

export interface LanguageConfig {
  code: LanguageCode;
  label: string;
  nativeLabel: string;
  flag: string;
  description: string;
  hreflang: string;
  seoTitle: string;
  seoDescription: string;
}

export const LANGUAGES: LanguageConfig[] = [
  {
    code: 'english',
    label: 'English',
    nativeLabel: 'English',
    flag: '🇬🇧',
    description: 'Most widely used language for typing tests',
    hreflang: 'en',
    seoTitle: 'English Typing Speed Test — WPM Test Online | TypeSwift',
    seoDescription:
      "Test your English typing speed online. Measure WPM, accuracy, and keystrokes with TypeSwift's free English typing test.",
  },
  {
    code: 'spanish',
    label: 'Spanish',
    nativeLabel: 'Español',
    flag: '🇪🇸',
    description: 'Practice typing in Spanish with accents',
    hreflang: 'es',
    seoTitle: 'Spanish Typing Speed Test — Prueba de Velocidad | TypeSwift',
    seoDescription:
      "Test your Spanish typing speed online. Mide tu velocidad con TypeSwift's free Spanish WPM test.",
  },
  {
    code: 'french',
    label: 'French',
    nativeLabel: 'Français',
    flag: '🇫🇷',
    description: 'Type French words with proper accents',
    hreflang: 'fr',
    seoTitle: 'French Typing Speed Test — Test de Vitesse | TypeSwift',
    seoDescription:
      "Test your French typing speed online. Measure WPM and accuracy with TypeSwift's free French typing test.",
  },
  {
    code: 'german',
    label: 'German',
    nativeLabel: 'Deutsch',
    flag: '🇩🇪',
    description: 'Practice German compound words and umlauts',
    hreflang: 'de',
    seoTitle: 'German Typing Speed Test — Tippgeschwindigkeit | TypeSwift',
    seoDescription:
      "Test your German typing speed online. Measure WPM and accuracy with TypeSwift's free German typing test.",
  },
  {
    code: 'hindi',
    label: 'Hindi',
    nativeLabel: 'हिंदी',
    flag: '🇮🇳',
    description: 'Type common Hindi words in Roman script',
    hreflang: 'hi',
    seoTitle: 'Hindi Typing Speed Test — WPM Test | TypeSwift',
    seoDescription:
      "Test your Hindi typing speed online. Measure WPM and accuracy with TypeSwift's free Hindi typing test.",
  },
  {
    code: 'portuguese',
    label: 'Portuguese',
    nativeLabel: 'Português',
    flag: '🇧🇷',
    description: 'Practice typing in Brazilian Portuguese',
    hreflang: 'pt',
    seoTitle: 'Portuguese Typing Test — Teste de Digitação | TypeSwift',
    seoDescription:
      "Test your Portuguese typing speed online. Measure WPM and accuracy with TypeSwift's free Portuguese typing test.",
  },
  {
    code: 'italian',
    label: 'Italian',
    nativeLabel: 'Italiano',
    flag: '🇮🇹',
    description: 'Type Italian words and common phrases',
    hreflang: 'it',
    seoTitle: 'Italian Typing Test — Test di Digitazione | TypeSwift',
    seoDescription:
      "Test your Italian typing speed online. Measure WPM and accuracy with TypeSwift's free Italian typing test.",
  },
  {
    code: 'russian',
    label: 'Russian',
    nativeLabel: 'Русский',
    flag: '🇷🇺',
    description: 'Practice typing Russian words in Cyrillic script',
    hreflang: 'ru',
    seoTitle: 'Russian Typing Test — Cyrillic WPM Test Online | TypeSwift',
    seoDescription:
      "Test your Russian typing speed online. Measure WPM, CPM, and accuracy in Cyrillic with TypeSwift's free Russian typing test.",
  },
];

/** Total number of supported languages — derive UI/SEO copy from this, never hardcode a number. */
export const LANGUAGE_COUNT = LANGUAGES.length;

/** Uppercase code summary for compact UI, e.g. "EN, ES, FR, DE, HI, PT, IT, RU". */
export const LANGUAGE_CODE_SUMMARY = LANGUAGES.map((l) => l.hreflang.toUpperCase()).join(', ');

/** Oxford-comma sentence of language names, e.g. "English, Spanish, …, and Russian". */
export const LANGUAGE_NAME_SENTENCE = (() => {
  const names = LANGUAGES.map((l) => l.label);
  return names.length > 1
    ? `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
    : names[0];
})();

export const WORD_LISTS: Record<LanguageCode, string[]> = {
  english: [
    'the',
    'be',
    'to',
    'of',
    'and',
    'a',
    'in',
    'that',
    'have',
    'it',
    'for',
    'not',
    'on',
    'with',
    'he',
    'as',
    'you',
    'do',
    'at',
    'this',
    'but',
    'his',
    'by',
    'from',
    'they',
    'we',
    'say',
    'her',
    'she',
    'or',
    'an',
    'will',
    'my',
    'one',
    'all',
    'would',
    'there',
    'their',
    'what',
    'so',
    'up',
    'out',
    'if',
    'about',
    'who',
    'get',
    'which',
    'go',
    'me',
    'when',
    'make',
    'can',
    'like',
    'time',
    'no',
    'just',
    'him',
    'know',
    'take',
    'people',
    'into',
    'year',
    'your',
    'good',
    'some',
    'could',
    'them',
    'see',
    'other',
    'than',
    'then',
    'now',
    'look',
    'only',
    'come',
    'its',
    'over',
    'think',
    'also',
    'back',
    'after',
    'use',
    'two',
    'how',
    'our',
    'work',
    'first',
    'well',
    'way',
    'even',
    'new',
    'want',
    'because',
    'any',
    'these',
    'give',
    'day',
    'most',
    'us',
    'between',
    'need',
    'large',
    'often',
    'hand',
    'high',
    'place',
    'hold',
    'turn',
    'help',
    'start',
    'city',
    'play',
    'small',
    'number',
    'off',
    'always',
    'move',
    'live',
    'point',
    'world',
    'around',
    'every',
    'name',
    'real',
    'life',
    'few',
    'north',
    'open',
    'seem',
    'together',
    'next',
    'white',
    'children',
    'begin',
    'got',
    'walk',
    'example',
    'ease',
    'paper',
    'group',
    'always',
    'music',
    'those',
    'both',
    'mark',
    'book',
    'letter',
    'until',
    'mile',
    'river',
    'car',
    'feet',
    'care',
    'second',
    'enough',
    'plain',
    'girl',
    'usual',
    'young',
    'ready',
    'above',
    'ever',
    'red',
    'list',
    'though',
    'feel',
    'talk',
    'bird',
    'soon',
    'body',
    'dog',
    'family',
    'direct',
    'pose',
    'leave',
    'song',
    'measure',
    'door',
    'product',
    'black',
    'short',
    'numeral',
  ],
  spanish: [
    'el',
    'la',
    'de',
    'que',
    'y',
    'a',
    'en',
    'un',
    'ser',
    'se',
    'no',
    'haber',
    'por',
    'con',
    'su',
    'para',
    'como',
    'estar',
    'tener',
    'le',
    'lo',
    'lo',
    'todo',
    'pero',
    'mas',
    'hacer',
    'o',
    'poder',
    'decir',
    'este',
    'ir',
    'otro',
    'ese',
    'si',
    'me',
    'ya',
    'ver',
    'porque',
    'dar',
    'cuando',
    'muy',
    'sin',
    'vez',
    'mucho',
    'saber',
    'que',
    'sobre',
    'mi',
    'alguno',
    'mismo',
    'yo',
    'también',
    'hasta',
    'año',
    'dos',
    'querer',
    'entre',
    'así',
    'primero',
    'desde',
    'grande',
    'eso',
    'ni',
    'nos',
    'llegar',
    'pasar',
    'tiempo',
    'ella',
    'sí',
    'día',
    'uno',
    'bien',
    'poco',
    'deber',
    'entonces',
    'poner',
    'cosa',
    'tanto',
    'hombre',
    'parecer',
    'nuestro',
    'tan',
    'donde',
    'ahora',
    'parte',
    'después',
    'vida',
    'quedar',
    'siempre',
    'creer',
    'hablar',
    'llevar',
    'dejar',
    'nada',
    'cada',
    'seguir',
    'menos',
    'nuevo',
    'encontrar',
    'solo',
    'casa',
    'agua',
    'libro',
    'ciudad',
    'trabajo',
    'mundo',
    'mano',
    'padre',
    'madre',
    'niño',
    'puerta',
    'luz',
    'mesa',
    'nombre',
    'cielo',
    'tierra',
    'amor',
    'camino',
    'mar',
    'sol',
  ],
  french: [
    'le',
    'de',
    'un',
    'être',
    'et',
    'à',
    'il',
    'avoir',
    'ne',
    'je',
    'son',
    'que',
    'se',
    'qui',
    'ce',
    'dans',
    'en',
    'du',
    'elle',
    'au',
    'les',
    'des',
    'sur',
    'par',
    'mais',
    'ou',
    'si',
    'leur',
    'y',
    'plus',
    'tout',
    'même',
    'pouvoir',
    'dire',
    'faire',
    'bien',
    'aussi',
    'sous',
    'vouloir',
    'très',
    'comme',
    'savoir',
    'voir',
    'aller',
    'non',
    'lui',
    'après',
    'bon',
    'temps',
    'venir',
    'deux',
    'homme',
    'femme',
    'maison',
    'eau',
    'livre',
    'ville',
    'travail',
    'monde',
    'main',
    'père',
    'mère',
    'enfant',
    'porte',
    'lumière',
    'table',
    'nom',
    'ciel',
    'terre',
    'amour',
    'chemin',
    'mer',
    'soleil',
    'nuit',
    'jour',
    'mois',
    'année',
    'vie',
    'mort',
    'place',
    'jardin',
    'fleur',
    'arbre',
    'chat',
    'chien',
    'couleur',
    'blanc',
    'noir',
    'rouge',
    'bleu',
    'grand',
    'petit',
    'beau',
    'vieux',
    'nouveau',
    'premier',
    'dernier',
    'seul',
    'autre',
    'même',
  ],
  german: [
    'der',
    'die',
    'und',
    'in',
    'den',
    'von',
    'zu',
    'das',
    'mit',
    'sich',
    'des',
    'auf',
    'für',
    'ist',
    'im',
    'dem',
    'nicht',
    'ein',
    'eine',
    'als',
    'auch',
    'es',
    'an',
    'werden',
    'aus',
    'er',
    'hat',
    'dass',
    'sie',
    'nach',
    'wird',
    'bei',
    'noch',
    'wie',
    'um',
    'am',
    'sind',
    'so',
    'einer',
    'war',
    'haben',
    'ich',
    'man',
    'aber',
    'vor',
    'durch',
    'kann',
    'du',
    'wir',
    'alle',
    'haus',
    'wasser',
    'buch',
    'stadt',
    'arbeit',
    'welt',
    'hand',
    'vater',
    'mutter',
    'kind',
    'tür',
    'licht',
    'tisch',
    'name',
    'himmel',
    'erde',
    'liebe',
    'weg',
    'meer',
    'sonne',
    'nacht',
    'tag',
    'monat',
    'jahr',
    'leben',
    'zeit',
    'platz',
    'garten',
    'blume',
    'baum',
    'katze',
    'hund',
    'farbe',
    'weiss',
    'schwarz',
    'rot',
    'blau',
    'gross',
    'klein',
    'neu',
    'alt',
    'gut',
    'schön',
    'erste',
    'letzte',
    'allein',
    'andere',
    'gleich',
    'kommen',
    'gehen',
  ],
  hindi: [
    'aur',
    'hai',
    'ka',
    'ki',
    'ke',
    'se',
    'ko',
    'ne',
    'par',
    'ye',
    'jo',
    'kya',
    'ek',
    'ho',
    'hain',
    'nahi',
    'to',
    'main',
    'ap',
    'us',
    'is',
    'tha',
    'koi',
    'sab',
    'woh',
    'log',
    'kuch',
    'bhi',
    'bahut',
    'hi',
    'tak',
    'ab',
    'phir',
    'jab',
    'toh',
    'lekin',
    'mera',
    'tera',
    'uska',
    'hamara',
    'pani',
    'ghar',
    'log',
    'desh',
    'kaam',
    'naam',
    'din',
    'raat',
    'samay',
    'dil',
    'man',
    'aaj',
    'kal',
    'saal',
    'mahina',
    'khana',
    'paisa',
    'rasta',
    'shahar',
    'gaon',
    'baat',
    'khet',
    'phool',
    'ped',
    'nadi',
    'pahad',
    'akash',
    'dharti',
    'surya',
    'chandra',
    'pyar',
    'dost',
    'bhai',
    'bahen',
    'mata',
    'pita',
    'beta',
    'beti',
    'geet',
    'kitab',
    'rang',
    'safed',
    'kala',
    'lal',
    'nila',
    'hara',
    'bada',
    'chota',
    'accha',
    'bura',
    'naya',
    'purana',
    'pehla',
    'akhri',
    'sahi',
    'galat',
    'zyada',
    'kam',
    'jaldi',
    'dheere',
  ],
  portuguese: [
    'o',
    'a',
    'de',
    'e',
    'do',
    'da',
    'em',
    'um',
    'para',
    'com',
    'uma',
    'os',
    'no',
    'se',
    'na',
    'por',
    'mais',
    'as',
    'dos',
    'como',
    'mas',
    'ao',
    'ele',
    'das',
    'seu',
    'sua',
    'ou',
    'quando',
    'muito',
    'nos',
    'já',
    'eu',
    'também',
    'só',
    'pelo',
    'pela',
    'até',
    'isso',
    'ela',
    'entre',
    'depois',
    'sem',
    'mesmo',
    'aos',
    'seus',
    'quem',
    'nas',
    'me',
    'esse',
    'eles',
    'casa',
    'água',
    'livro',
    'cidade',
    'trabalho',
    'mundo',
    'mão',
    'pai',
    'mãe',
    'filho',
    'porta',
    'luz',
    'mesa',
    'nome',
    'céu',
    'terra',
    'amor',
    'caminho',
    'mar',
    'sol',
    'noite',
    'dia',
    'mês',
    'ano',
    'vida',
    'tempo',
    'lugar',
    'jardim',
    'flor',
    'árvore',
    'gato',
    'cachorro',
    'cor',
    'branco',
    'preto',
    'vermelho',
    'azul',
    'grande',
    'pequeno',
    'novo',
    'velho',
    'bom',
    'lindo',
    'primeiro',
    'último',
    'sozinho',
    'outro',
    'mesmo',
    'vir',
    'ir',
  ],
  italian: [
    'il',
    'di',
    'e',
    'la',
    'che',
    'in',
    'un',
    'a',
    'per',
    'non',
    'del',
    'si',
    'le',
    'con',
    'una',
    'da',
    'questo',
    'ma',
    'mi',
    'ho',
    'lo',
    'ha',
    'ci',
    'al',
    'come',
    'più',
    'anche',
    'io',
    'lui',
    'lei',
    'suo',
    'sua',
    'sono',
    'era',
    'hai',
    'già',
    'se',
    'quello',
    'sta',
    'tra',
    'qui',
    'così',
    'bene',
    'dopo',
    'quando',
    'ancora',
    'sempre',
    'molto',
    'tutto',
    'fatto',
    'casa',
    'acqua',
    'libro',
    'città',
    'lavoro',
    'mondo',
    'mano',
    'padre',
    'madre',
    'figlio',
    'porta',
    'luce',
    'tavolo',
    'nome',
    'cielo',
    'terra',
    'amore',
    'strada',
    'mare',
    'sole',
    'notte',
    'giorno',
    'mese',
    'anno',
    'vita',
    'tempo',
    'posto',
    'giardino',
    'fiore',
    'albero',
    'gatto',
    'cane',
    'colore',
    'bianco',
    'nero',
    'rosso',
    'blu',
    'grande',
    'piccolo',
    'nuovo',
    'vecchio',
    'buono',
    'bello',
    'primo',
    'ultimo',
    'solo',
    'altro',
    'stesso',
    'venire',
    'andare',
  ],
  russian: [
    'и',
    'в',
    'не',
    'на',
    'я',
    'быть',
    'он',
    'с',
    'что',
    'а',
    'весь',
    'это',
    'как',
    'она',
    'по',
    'но',
    'они',
    'к',
    'у',
    'ты',
    'из',
    'мы',
    'за',
    'вы',
    'так',
    'же',
    'от',
    'сказать',
    'этот',
    'который',
    'мочь',
    'человек',
    'один',
    'ещё',
    'бы',
    'такой',
    'только',
    'себя',
    'свой',
    'какой',
    'когда',
    'уже',
    'для',
    'вот',
    'кто',
    'да',
    'говорить',
    'год',
    'знать',
    'мой',
    'до',
    'или',
    'если',
    'время',
    'рука',
    'нет',
    'самый',
    'стать',
    'большой',
    'даже',
    'другой',
    'наш',
    'где',
    'дело',
    'есть',
    'сам',
    'раз',
    'чтобы',
    'два',
    'там',
    'чем',
    'глаз',
    'жизнь',
    'первый',
    'день',
    'тут',
    'ничто',
    'потом',
    'очень',
    'хотеть',
    'при',
    'голова',
    'надо',
    'без',
    'видеть',
    'идти',
    'теперь',
    'тоже',
    'стоять',
    'друг',
    'дом',
    'сейчас',
    'можно',
    'после',
    'слово',
    'здесь',
    'думать',
    'место',
    'город',
    'работа',
    'мир',
    'отец',
    'мать',
    'ребёнок',
    'дверь',
    'свет',
    'стол',
    'имя',
    'небо',
    'земля',
    'любовь',
    'дорога',
    'море',
    'солнце',
    'ночь',
    'вода',
    'книга',
    'цвет',
    'белый',
    'чёрный',
    'красный',
    'синий',
    'новый',
    'старый',
    'хороший',
    'маленький',
    'вопрос',
    'страна',
    'сторона',
    'ждать',
  ],
};

export type TestDuration = 30 | 60 | 120 | 300;
export const TEST_DURATIONS: { value: TestDuration; label: string }[] = [
  { value: 30, label: '30s' },
  { value: 60, label: '1 min' },
  { value: 120, label: '2 min' },
  { value: 300, label: '5 min' },
];

export function getShuffledWords(language: LanguageCode, count = 120): string[] {
  const words = [...WORD_LISTS[language]];
  const result: string[] = [];
  while (result.length < count) {
    const shuffled = [...words].sort(() => 0.5 - Math.random());
    result.push(...shuffled);
  }
  return result.slice(0, count);
}

export function getLanguageConfig(code: LanguageCode): LanguageConfig {
  return LANGUAGES.find((l) => l.code === code) || LANGUAGES[0];
}
